import { Experiments } from 'constants/experiments'
import { deleteCookie, getCookies } from 'lib/web/cookieUtils'
import {
  PURPOSE_IDENTIFIER_COOKIE_PREFIXES,
  PURPOSE_IDENTIFIER_STORAGE_PREFIXES,
  SHARED_IDENTITY_COOKIE_PREFIXES,
  matchesIdentifierPrefix,
} from '../../lib/consent/identifierPrefixes'
import { canRetainTrackingIdentity } from '../../lib/consent/trackingIdentity'
import type { TrackingIdentity } from '../../lib/consent/trackingIdentity'
import { CONSENT_PURPOSES } from './types'
import type { ConsentPurpose } from './types'

function deleteCookieEverywhere(name: string) {
  const hostnameParts = window.location.hostname.split('.')
  const domains = hostnameParts.slice(0, -1).map((_, index) => hostnameParts.slice(index).join('.'))
  const attempts = [
    () => deleteCookie(name, '/'),
    () => deleteCookie(name),
    ...domains.map(domain => () => {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=${domain}`
    }),
  ]
  attempts.forEach(remove => {
    try {
      remove()
    } catch {
      // One blocked domain must not prevent the remaining deletion attempts.
    }
  })
}

function clearStorageIdentifiers(storageName: 'localStorage' | 'sessionStorage', prefixes: ReadonlyArray<string>) {
  try {
    const storage = window[storageName]
    const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter((key): key is string => !!key)
    keys.forEach(key => {
      if (matchesIdentifierPrefix(key, prefixes)) {
        try {
          storage.removeItem(key)
        } catch {
          // Keep attempting the remaining keys if one deletion is blocked.
        }
      }
    })
  } catch {
    // Storage getters and enumeration can throw when browser storage is blocked.
  }
}

const SHARED_IDENTITIES = Object.keys(SHARED_IDENTITY_COOKIE_PREFIXES) as Array<TrackingIdentity>

// Trigger cookies for retired Optimizely experiments that browsers may still hold.
const RETIRED_OPTIMIZELY_TRIGGER_COOKIES = ['search_experiences_nls_triggered'] as const

export function clearUngrantedPurposeIdentifiers(grantedPurposes: Array<ConsentPurpose>) {
  if (typeof window === 'undefined') return
  const deniedPurposes = CONSENT_PURPOSES.filter(purpose => !grantedPurposes.includes(purpose))
  const cookiePrefixes = deniedPurposes.flatMap(purpose => PURPOSE_IDENTIFIER_COOKIE_PREFIXES[purpose])
  const storagePrefixes = deniedPurposes.flatMap(purpose => PURPOSE_IDENTIFIER_STORAGE_PREFIXES[purpose])

  try {
    Object.keys(getCookies()).forEach(name => {
      const sharedIdentity = SHARED_IDENTITIES.find(identity => matchesIdentifierPrefix(name, SHARED_IDENTITY_COOKIE_PREFIXES[identity]))
      const shouldDelete = sharedIdentity
        ? !canRetainTrackingIdentity(grantedPurposes, sharedIdentity)
        : matchesIdentifierPrefix(name, cookiePrefixes)
      if (shouldDelete) deleteCookieEverywhere(name)
    })
  } catch {
    // Cookie access can fail independently of local/session storage.
  }

  if (deniedPurposes.includes('experimentation')) {
    // Optimizely writes `${flagKey}_triggered` with no provider prefix.
    Object.values(Experiments).forEach(experiment => {
      if (experiment.provider !== 'cherto') deleteCookieEverywhere(`${experiment.id}_triggered`)
    })
    RETIRED_OPTIMIZELY_TRIGGER_COOKIES.forEach(deleteCookieEverywhere)
  }

  clearStorageIdentifiers('localStorage', storagePrefixes)
  clearStorageIdentifiers('sessionStorage', storagePrefixes)
}
